<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col class="d-flex align-center">
          <v-breadcrumbs :items="breadcrumbs" class="px-0 py-0" />
          <v-spacer />
          <div class="ml-4 text-right">
            <v-tooltip max-width="300" bottom>
              <template #activator="{ on }">
                <base-chip
                  v-show="hasChanges"
                  :color="missingRequiredDetails.length ? 'warning' : 'blue'"
                  text="Changes Detected"
                  icon="info"
                  dark
                  small
                  v-on="on"
                >
                </base-chip>
              </template>
              <span v-if="missingRequiredDetails.length"
                >Missing required fields: {{ missingRequiredDetails.join() }}</span
              >
              <span v-else>Remember to save your changes before leaving!</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
      <v-divider class="mt-1" />
    </v-container>
    <v-container>
      <v-row v-if="parsedFile.length" class="mt-0 sticky">
        <v-col class="pt-0">
          <csv-table :rows="parsedFile" />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="8">
          <v-stepper v-model="step" class="stepper" vertical>
            <v-stepper-step :complete="step > 1 || isExistingReport" :editable="stepEditable(1)" step="1">
              Basic Details
            </v-stepper-step>

            <v-stepper-content step="1">
              <v-card outlined>
                <v-card-text>
                  <v-col sm="8">
                    <autocomplete-input
                      v-model="report.partner_slug"
                      :items="partners"
                      label="Partner"
                      class="mb-2"
                      hint="*Required"
                      persistent-hint
                      required
                    />
                    <autocomplete-input
                      v-model="report.deal_id"
                      :disabled="!report.partner_slug"
                      :items="dealsForPartner"
                      class="mb-2"
                      :hint="report.source === 'csv' ? '*Required' : null"
                      persistent-hint
                      :required="report.source === 'csv'"
                      label="Deal"
                    />
                    <autocomplete-input
                      v-model="report.source"
                      :items="reportOptions.sources"
                      label="Report Source"
                      class="mb-2"
                      hint="*Required"
                      persistent-hint
                      required
                    />
                    <autocomplete-input
                      v-model="report.type"
                      :items="reportOptions.types"
                      label="Type"
                      class="mb-2"
                      hint="*Required"
                      persistent-hint
                      required
                    />
                    <autocomplete-input
                      v-model="report.portal_id"
                      :items="portals"
                      label="Portal"
                      class="mb-2"
                      hint="*Required"
                      persistent-hint
                      required
                    />
                  </v-col>

                  <v-btn :disabled="!stepComplete(1) || isLoading" color="primary" depressed @click="step = 2">
                    Continue
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <v-stepper-step :complete="step > 2 || isExistingReport" :editable="stepEditable(2)" step="2">
              Report Instructions
              <small>Steps to retrieve the report, and any other notes</small>
            </v-stepper-step>

            <v-stepper-content step="2">
              <v-card outlined>
                <v-card-text>
                  <select-list
                    v-model="report.check_frequency"
                    :items="checkFrequencies"
                    label="Check Frequency"
                    class="mb-6"
                    hide-details
                  />
                  <item-list v-model="report.steps" :disabled="isLoading" label="Steps" button-label="Add step" />
                  <textarea-input v-model="report.notes" class="mt-4" label="Notes" />
                  <v-btn :disabled="!stepComplete(2) || isLoading" color="primary" depressed @click="onClickStepTwo">
                    Continue
                  </v-btn>
                  <v-btn :disabled="isLoading" text @click="step = 1">
                    back
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <v-stepper-step :complete="step > 3 || isExistingReport" :editable="stepEditable(3)" step="3">
              CSV Upload
              <small>Upload a CSV to configure the report</small>
            </v-stepper-step>

            <v-stepper-content step="3">
              <v-card outlined>
                <v-card-text>
                  <drag-and-drop-upload v-model="file" class="mb-4" />
                  <v-btn :disabled="!stepComplete(3) || isLoading" color="primary" depressed @click="step = 4">
                    Continue
                  </v-btn>
                  <v-btn text :disabled="isLoading" @click="step = 2">
                    back
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <v-stepper-step
              :complete="(step > 4 || isExistingReport) && !!parsedFile.length"
              :editable="stepEditable(4) && !!parsedFile.length"
              step="4"
            >
              <div class="d-flex align-center" :class="{ 'mb-1': isExistingReport && !parsedFile.length }">
                <span class="mr-4">Configuration</span>
                <v-tooltip v-if="isExistingReport && !parsedFile.length" right>
                  <template #activator="{ on }">
                    <v-icon small v-on="on">info</v-icon>
                  </template>
                  Upload a CSV to enable filter and rule configuration
                </v-tooltip>
              </div>
              <small>Map columns and select date format</small>
            </v-stepper-step>
            <v-stepper-content step="4">
              <v-card outlined>
                <v-card-text class="pt-0">
                  <v-col sm="8">
                    <v-subheader class="mb-3">Column Format</v-subheader>
                    <select-list
                      v-model="report.date_format"
                      :items="reportOptions.dateFormats"
                      label="Date Format"
                      :error-messages="!report.date_format ? 'Property Required!' : null"
                    />
                  </v-col>
                </v-card-text>
              </v-card>
              <v-card outlined class="mt-4">
                <v-card-text v-if="csvColumns.length" class="pt-0">
                  <v-col sm="8">
                    <v-subheader class="mb-3">
                      <span class="mr-2">Column Configuration</span>
                      <v-tooltip right>
                        <template #activator="{ on }">
                          <v-icon small v-on="on">info</v-icon>
                        </template>
                        <span
                          >Atleast one of the following columns are required: Registration Date, FTD Date &amp; QFTD
                          Date.</span
                        >
                      </v-tooltip>
                    </v-subheader>
                    <select-list
                      v-for="select in configureColumnSelectLists"
                      :key="select.model"
                      v-model="report.columns[select.model]"
                      :items="csvColumns"
                      :label="select.label"
                      :multiple="!!select.multiple"
                      :error-messages="getConfigureColumnError(select.model)"
                      class="mb-2"
                      hide-select-all
                      clearable
                    />
                  </v-col>
                  <v-btn :disabled="!stepComplete(4) || isLoading" color="primary" depressed @click="step = 5">
                    Continue
                  </v-btn>
                  <v-btn text :disabled="isLoading" @click="step = 3">
                    back
                  </v-btn>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <v-stepper-step
              :complete="(step > 5 || isExistingReport) && !!parsedFile.length"
              :editable="stepEditable(5) && !!parsedFile.length"
              step="5"
            >
              Configure filters
              <small>(optional) Only read rows that match these filters</small>
            </v-stepper-step>
            <v-stepper-content step="5">
              <v-card outlined>
                <v-card-text>
                  <v-simple-table v-if="report.filters.length" class="mb-4" dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Rule</th>
                          <th class="text-left">Column</th>
                          <th class="text-left">Values</th>
                          <th class="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(filter, index) in report.filters" :key="index">
                          <td>{{ formattedReportOption('rules', filter.key) }}</td>
                          <td>{{ filter.args.column || '-' }}</td>
                          <td>{{ filter.args.values.join(', ') || '-' }}</td>
                          <td class="text-right">
                            <v-btn
                              :disabled="isLoading"
                              class="white--text"
                              color="error"
                              depressed
                              x-small
                              title="Delete"
                              @click="onClickDeleteFilter(index)"
                              ><v-icon x-small>delete_outline</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-btn :disabled="isLoading" class="mb-4" depressed small @click="showAddFilterModal = true">
                    Add Filter
                  </v-btn>
                  <div>
                    <v-btn
                      :disabled="!stepComplete(5) || isLoading"
                      color="primary"
                      depressed
                      @click="step = report.type === 'combined' ? 6 : 7"
                    >
                      Continue
                    </v-btn>
                    <v-btn text :disabled="isLoading" @click="step = 4">
                      back
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-stepper-content>

            <v-stepper-step
              :complete="(step > 6 || isExistingReport) && report.type === 'combined' && !!parsedFile.length"
              :editable="stepEditable(6) && !!parsedFile.length && report.type === 'combined'"
              step="6"
            >
              Configure Rules
              <small>(optional) Apply rules that determine event types for combined reports</small>
            </v-stepper-step>
            <v-stepper-content step="6">
              <v-card v-for="({ label, value: type }, i) in eventTypes" :key="type" :class="{ 'mt-4': !!i }" outlined>
                <v-card-title>{{ label }}</v-card-title>
                <v-card-text>
                  <v-simple-table class="mb-4" dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">Rule</th>
                          <th class="text-left">Column</th>
                          <th class="text-left">Values</th>
                          <th class="text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-if="report.rules[type]">
                          <tr v-for="(rule, index) in report.rules[type]" :key="`${rule.args.key}-${index}`">
                            <td>{{ formattedReportOption('rules', rule.key) }}</td>
                            <td>{{ rule.args.column || '-' }}</td>
                            <td>{{ rule.args.values.join(', ') || '-' }}</td>
                            <td class="text-right">
                              <v-btn
                                :disabled="isLoading"
                                class="white--text"
                                color="error"
                                depressed
                                x-small
                                title="Delete"
                                @click="onClickDeleteRule(type, index)"
                                ><v-icon x-small>delete_outline</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </template>
                  </v-simple-table>
                  <v-btn class="mb-4" :disabled="isLoading" depressed small @click="onAddRule(type)">
                    Add Rule
                  </v-btn>
                </v-card-text>
              </v-card>
              <div class="mt-4">
                <v-btn :disabled="!stepComplete(6) || isLoading" color="primary" depressed @click="step = 7">
                  Continue
                </v-btn>
                <v-btn text :disabled="isLoading" @click="step = 5">
                  back
                </v-btn>
              </div>
            </v-stepper-content>

            <template v-if="!isExistingReport">
              <v-stepper-step :complete="step > 7" step="7">
                Review Integration
                <small>Confirm that the integration has been configured correctly</small>
              </v-stepper-step>
              <v-stepper-content step="7">
                <v-card outlined>
                  <v-card-text>
                    <div>
                      <v-btn color="primary" :disabled="isLoading" depressed @click="onSaveConfirm">
                        Save Integration
                      </v-btn>
                      <v-btn text :disabled="isLoading" @click="step = report.source === 'csv' ? 6 : 2">
                        back
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>
              </v-stepper-content>
            </template>
          </v-stepper>
        </v-col>

        <v-col sm="4">
          <v-card>
            <v-card-title>Integration Overview</v-card-title>
            <v-card-text class="overview">
              <div>Details</div>
              <v-simple-table dense>
                <template v-slot:default>
                  <tbody>
                    <tr v-for="({ label, dataFormat, optionFormat, model }, index) in reportDetailsFields" :key="index">
                      <th>{{ label }}</th>
                      <td class="text-right text-caption">
                        <div class="d-inline-flex">
                          <span v-if="dataFormat">{{ formattedData(dataFormat, report[model]) }}</span>
                          <span v-else-if="optionFormat">{{ formattedReportOption(optionFormat, report[model]) }}</span>
                          <span v-else>{{ report[model] }}</span>
                          <v-icon v-if="isModifiedValue(model)" color="blue" class="ml-1" small>info</v-icon>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <div v-if="step > 1 || isExistingReport">
                <div class="mt-4">Report information</div>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr class="table-row">
                        <th>Check Frequency</th>
                        <td class="text-right text-caption">
                          <div class="d-inline-flex">
                            <span>{{ formattedReportOption('checkFrequencies', report.check_frequency) }}</span>
                            <v-icon v-if="isModifiedValue('check_frequency')" color="blue" class="ml-1" small
                              >info</v-icon
                            >
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div class="table-row px-4 pb-4">
                  <div class="caption font-weight-bold">Steps</div>
                  <ol>
                    <li v-for="(step, index) in report.steps" :key="index" class="text-caption">
                      <div class="d-flex">
                        <span>{{ step }}</span>
                        <v-spacer />
                        <v-icon v-if="isModifiedValue(index, 'steps')" color="blue" class="ml-1" small>info</v-icon>
                      </div>
                    </li>
                  </ol>
                </div>
                <div class="table-row table-row--last pa-4">
                  <div class="caption font-weight-bold">Notes</div>
                  <p class="text-caption mb-0">{{ report.notes }}</p>
                </div>
              </div>

              <template v-if="(step > 3 || isExistingReport) && report.source === 'csv'">
                <div class="mt-4">Columns</div>
                <v-simple-table dense>
                  <template v-slot:default>
                    <tbody>
                      <tr>
                        <th>Date Format</th>
                        <td class="text-right text-caption">
                          <div class="d-inline-flex">
                            <div>
                              <div>{{ formattedReportOption('dateFormats', report.date_format) }}</div>
                              <div>({{ report.date_format }})</div>
                            </div>
                            <v-tooltip v-if="isModifiedValue('date_format')" bottom>
                              <template #activator="{ on }">
                                <v-icon color="blue" class="ml-1" small v-on="on">info</v-icon>
                              </template>
                              <span>Detected changes</span>
                            </v-tooltip>
                          </div>
                        </td>
                      </tr>
                      <tr v-for="{ label, model } in configureColumnSelectLists" :key="model">
                        <th>{{ label }}</th>
                        <td class="text-right text-caption">
                          <div class="d-inline-flex">
                            <span>{{
                              Array.isArray(report.columns[model])
                                ? report.columns[model].join(', ')
                                : report.columns[model]
                            }}</span>
                            <v-tooltip v-if="isModifiedValue(model, 'columns')" bottom>
                              <template #activator="{ on }">
                                <v-icon color="blue" class="ml-1" small v-on="on">info</v-icon>
                              </template>
                              <span>Detected changes</span>
                            </v-tooltip>
                            <v-tooltip v-if="getConfigureColumnError(model)" bottom>
                              <template #activator="{ on }">
                                <v-icon color="error" class="ml-1" small v-on="on">error</v-icon>
                              </template>
                              <span>{{ getConfigureColumnError(model) }}</span>
                            </v-tooltip>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </template>

              <template v-if="report.filters.length || (originalReport && originalReport.filters.length)">
                <div class="mt-4">
                  <span>Filters</span>
                  <v-tooltip v-if="originalReport && !isEqual(report.filters, originalReport.filters)" bottom>
                    <template #activator="{ on }">
                      <v-icon color="blue" class="ml-1" small v-on="on">info</v-icon>
                    </template>
                    <span>Detected changes</span>
                  </v-tooltip>
                </div>
                <v-list>
                  <template v-for="({ key, args: { column, values } }, index) in report.filters">
                    <v-divider v-if="!!index" :key="`${key}-${column}-${index}-divider`" />
                    <v-list-item :key="`${key}-${column}-${index}`" class="d-block">
                      <v-list-item-content class="text-right text-caption d-block">
                        <div class="d-flex justify-between">
                          <span class="font-weight-bold">Rule:</span>
                          <v-spacer />
                          <span>{{ formattedReportOption('rules', key) }}</span>
                        </div>
                        <div v-if="column" class="d-flex justify-between">
                          <span class="font-weight-bold">Column:</span>
                          <v-spacer />
                          <span>{{ column }}</span>
                        </div>
                        <div v-if="values && values.length" class="d-flex justify-between">
                          <span class="font-weight-bold">Value(s):</span>
                          <v-spacer />
                          <span>{{ Array.isArray(values) ? values.join(', ') : values }}</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </template>

              <template v-if="showRulesSummary">
                <div class="mt-4">
                  <span>Rules</span>
                  <v-tooltip v-if="originalReport && !isEqual(report.rules, originalReport.rules)" bottom>
                    <template #activator="{ on }">
                      <v-icon color="blue" class="ml-1" small v-on="on">info</v-icon>
                    </template>
                    <span>Detected changes</span>
                  </v-tooltip>
                </div>
                <template v-for="{ label, value: type } in eventTypes">
                  <v-list v-if="report.rules[type] && report.rules[type].length" :key="type">
                    <div class="font-weight-bold text-caption">{{ label }}</div>
                    <template v-for="({ key, args: { column, values } }, index) in report.rules[type]">
                      <v-divider v-if="!!index" :key="`${key}-${column}-${index}-divider`" />
                      <v-list-item :key="`${key}-${column}-${index}`" class="d-block">
                        <v-list-item-content class="text-right text-caption d-block">
                          <div class="d-flex justify-between">
                            <span class="font-weight-bold">Rule:</span>
                            <v-spacer />
                            <span>{{ formattedReportOption('rules', key) }}</span>
                          </div>
                          <div v-if="column" class="d-flex justify-between">
                            <span class="font-weight-bold">Column:</span>
                            <v-spacer />
                            <span>{{ column }}</span>
                          </div>
                          <div v-if="values && values.length" class="d-flex justify-between">
                            <span class="font-weight-bold">Value(s):</span>
                            <v-spacer />
                            <span>{{ Array.isArray(values) ? values.join(', ') : values }}</span>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                    </template>
                  </v-list>
                </template>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <fab-actions
      v-if="isExistingReport"
      :can-delete="isPermittedTo('delete')"
      :can-edit="isPermittedTo('update')"
      :has-changes="hasChanges"
      context="Report"
      @delete="showDeleteModal = true"
      @reset="showResetModal = true"
      @save="showSaveModal = true"
    />

    <!-- Filter Modal -->
    <generic-modal v-model="showAddFilterModal" title="Add Filter" hide-buttons>
      <v-col class="pa-0" sm="7">
        <rule-setter
          v-model="newFilter"
          :columns="csvColumns"
          @confirm="onAddFilterConfirm"
          @cancel="onAddFilterCancel"
        />
      </v-col>
    </generic-modal>

    <!-- Rule Modal -->
    <generic-modal v-model="showAddRuleModal" title="Add Rule" hide-buttons>
      <v-col class="pa-0" sm="7">
        <rule-setter v-model="newRule" :columns="csvColumns" @confirm="onAddRuleConfirm" @cancel="onAddRuleCancel" />
      </v-col>
    </generic-modal>

    <!-- Reset Modal -->
    <reset-modal
      v-model="showResetModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      category="partner report"
      @modal-confirm="onResetConfirm"
    />

    <!-- Save Modal -->
    <generic-modal
      v-model="showSaveModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Save Article"
      persistent
      :is-confirm-disabled="!!missingRequiredDetails.length"
      @modal-confirm="onSaveConfirm"
      @modal-cancel="showSaveModal = false"
    >
      <span v-if="missingRequiredDetails.length">
        Missing Required Fields: <span class="font-weight-bold">{{ missingRequiredDetails.join() }}</span>
      </span>
      <span v-else>Are you sure you want to save this Partner Report?</span>
    </generic-modal>

    <!-- Delete Modal -->
    <generic-modal
      v-model="showDeleteModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      title="Delete Partner Report"
      confirm-color="error"
      confirm-text="Delete"
      @modal-confirm="onDeleteConfirm"
    >
      <div>
        Are you sure you want to <span class="red--text font-weight-bold">DELETE</span> this Partner REport? This cannot
        be undone.
      </div>
    </generic-modal>

    <!-- Exit Page Modal -->
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
  </page-loader>
</template>

<script>
// Vue
import { mapGetters, mapMutations } from 'vuex'
// Helpers
import { readCsv } from '@/utils/csv'
import { cloneDeep, isEqual } from 'lodash-es'
import isSet from '@/utils/is-set'
// Mixins
import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import SelectList from '@/components/forms/select-list'
import BaseChip from '@/components/chips/base-chip'
import CsvTable from '@/views/data/components/csv-table'
import DragAndDropUpload from '@/components/drag-and-drop-upload'
import FabActions from '@/components/fab-actions'
import GenericModal from '@/components/modals/generic-modal'
import ItemList from '@/components/forms/item-list'
import PageLoader from '@/components/page-loader'
import ResetModal from '@/components/modals/reset-modal'
import RuleSetter from '@/views/data/components/rule-setter'
import TextareaInput from '@/components/forms/textarea-input'
// Services
import { deals, partnerPortals, partnerReports, partners } from '@/services/offers'

const newRule = ({ key, column = null, values = [] } = {}) => ({
  key,
  args: {
    column,
    values,
  },
})

export default {
  name: 'ManagePartnerReport',
  components: {
    AutocompleteInput,
    SelectList,
    BaseChip,
    CsvTable,
    DragAndDropUpload,
    FabActions,
    GenericModal,
    ItemList,
    PageLoader,
    ResetModal,
    RuleSetter,
    TextareaInput,
  },
  mixins: [DataModification, PermissionsHandler],
  data: () => ({
    isLoading: false,
    step: 1,
    eventTypes: [
      { label: 'Registration', value: 'registration' },
      { label: 'FTD', value: 'ftd' },
      { label: 'QFTD', value: 'qftd' },
      { label: 'Sale', value: 'sale' },
      { label: 'Basket', value: 'basket' },
    ],
    originalReport: null,
    report: {
      source: null,
      partner_slug: null,
      deal_id: null,
      portal_id: null,
      type: null,
      date_format: null,
      columns: {
        registration_date: null,
        ftd_date: null,
        qftd_date: null,
        sale_date: null,
        basket_date: null,
        basket_date: null,
        click_id: [],
        commission: null,
        country: null,
        offer: null,
        website: null,
      },
      steps: [],
      rules: {},
      filters: [],
      notes: null,
    },
    configureColumnSelectLists: [
      { model: 'registration_date', label: 'Registration Date' },
      { model: 'ftd_date', label: 'FTD Date' },
      { model: 'qftd_date', label: 'QFTD Date' },
      { model: 'sale_date', label: 'Sale Date' },
      { model: 'basket_date', label: 'Basket Date' },
      { model: 'click_id', label: 'Click ID', multiple: true },
      { model: 'commission', label: 'Comission' },
      { model: 'country', label: 'Country' },
      { model: 'offer', label: 'Offer' },
      { model: 'website', label: 'Website' },
    ],
    deals: [],
    partners: [],
    portals: [],
    file: null,
    parsedFile: [],
    showAddFilterModal: false,
    showAddRuleModal: false,
    newRuleType: null,
    newFilter: newRule(),
    newRule: newRule(),
    showDeleteModal: false,
    showSaveModal: false,
    showResetModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption', 'hasOptions']),
    breadcrumbs() {
      return [
        { text: 'Reports & Ledger', to: { name: 'data.ledger-beta' }, exact: true },
        { text: this.report.id || 'New Report', disabled: true },
      ]
    },
    checkFrequencies() {
      return this.getItemsForOption('reports')?.checkFrequencies
    },
    configureColumnErrors() {
      if (!this.report?.columns || !this.parsedFile.length) return []

      return Object.entries(this.report.columns).reduce((acc, [key, reportColumnValue]) => {
        const columnValueNotValid = this.csvColumns.find(({ value: csvOption, disabled }) => {
          if (Array.isArray(reportColumnValue)) return reportColumnValue.includes(csvOption) && disabled
          return reportColumnValue === csvOption && disabled
        })
        if (columnValueNotValid) acc.push(key)
        return acc
      }, [])
    },
    isExistingReport() {
      return !!this.$route?.params?.id
    },
    hasChanges() {
      if (!this.originalReport || !this.report.id) return false
      return this.hasDataChanged(this.originalReport, this.report)
    },
    missingRequiredDetails() {
      return this.reportDetailsFields.reduce((acc, { required, model, label }) => {
        if (required && !isSet(this.report[model])) acc.push(label)
        return acc
      }, [])
    },
    reportDetailsFields() {
      return [
        {
          label: 'Partner',
          dataFormat: 'partners',
          model: 'partner_slug',
          required: true,
        },
        {
          label: 'Deal',
          dataFormat: 'dealsForPartner',
          model: 'deal_id',
          required: this.report.source === 'csv',
        },
        {
          label: 'Source',
          optionFormat: 'sources',
          model: 'source',
          required: true,
        },
        {
          label: 'Type',
          optionFormat: 'types',
          model: 'type',
          required: true,
        },
        {
          label: 'Portal',
          dataFormat: 'portals',
          model: 'portal_id',
          required: true,
        },
      ]
    },
    reportOptions() {
      return this.getItemsForOption('reports')
    },
    dealsForPartner() {
      if (!this.report.partner_slug) return []
      return this.deals
        .filter(({ partner }) => this.report.partner_slug === partner)
        .map(({ name: key, id: value }) => ({ key, value }))
    },
    csvColumns() {
      // Get existing columns from when report was last configured.
      // These should be disabled. Only new columns should be selected.
      const existingColumns = this.report?.columns
        ? Object.values(this.report.columns).reduce((acc, column) => {
            if (column && column.length) {
              if (Array.isArray(column)) {
                column.forEach(value => {
                  acc.push({ key: value, value, disabled: true })
                })
              } else {
                acc.push({ key: column, value: column, disabled: true })
              }
            }
            return acc
          }, [])
        : []

      // Get new columns from uploaded CSV.
      let newColumns = []
      if (this.parsedFile.length) {
        const [row] = this.parsedFile
        newColumns = Object.keys(row).map(value => ({ key: value, value }))
      }

      // Remove duplicate columns.
      return [...newColumns, ...existingColumns].reduce((acc, column) => {
        if (!acc.find(searchCol => searchCol.value === column.value)) acc.push(column)
        return acc
      }, [])
    },
    showRulesSummary() {
      return (
        this.report.rules?.registration?.length ||
        this.report.rules?.ftd?.length ||
        this.report.rules?.qftd?.length ||
        this.originalReport?.rules?.registration?.length ||
        this.originalReport?.rules?.ftd?.length ||
        this.originalReport?.rules?.qftd?.length
      )
    },
  },
  watch: {
    'report.partner'() {
      if (this.originalReport?.partner_slug === this.report.partner_slug) return
      this.report.deal_id = null
    },
    async file() {
      if (!this.file) return
      const parsed = await readCsv(this.file)
      this.parsedFile = parsed
    },
  },
  async created() {
    if (this.$route?.params?.id) {
      await this.getReport()
    }
    await Promise.all([this.getDeals(), this.getPartners(), this.getPortals()])
    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.setPageLoading(false)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasChanges) return next()
    this.handleExitBeforeSave(next)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    isEqual,
    async getDeals() {
      this.isLoading = true
      try {
        this.deals = await deals.getActiveDeals()
      } catch (error) {
        console.log(error)
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve deals',
        })
      }

      this.isLoading = false
    },
    async getPartners() {
      try {
        const { data } = await partners.getAllPartners({ compact: true })
        this.partners = data.map(({ name: key, slug: value }) => ({ key, value }))
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Partners',
        })
      }
    },
    async getPortals() {
      try {
        const { data } = await partnerPortals.getFilteredPortals()
        this.portals = data.map(({ name: key, id: value }) => ({ key, value }))
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Partner Portals',
        })
      }
    },
    async createReport() {
      this.isLoading = true

      try {
        await partnerReports.createReport(this.report)
        this.$notify({
          type: 'success',
          text: 'Successfully created Partner Report',
        })
        this.$router.push({ name: 'data.ledger-beta' })
      } catch (error) {
        console.log(error)
        this.$notify({
          type: 'error',
          text: 'Unable to save Partner Report',
        })
      }

      this.isLoading = false
    },
    async getReport() {
      try {
        this.originalReport = await partnerReports.getSingleReport(this.$route.params.id)
        this.report = cloneDeep(this.originalReport)
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Partner Report.',
        })
        this.$router.push({ name: 'data.ledger-beta' })
      }
    },
    isModifiedValue(key, parentKey = null) {
      if (!this.originalReport) return false
      if (parentKey) {
        return !isEqual(this.originalReport[parentKey][key], this.report[parentKey][key])
      }
      return !isEqual(this.originalReport[key], this.report[key])
    },
    onAddRule(type) {
      this.newRuleType = type
      this.showAddRuleModal = true
    },
    onBeforeUnload(event) {
      if (!this.hasChanges) return
      this.confirmUnload(event)
    },
    async onDeleteConfirm() {
      this.isLoading = true

      try {
        await partnerReports.deleteReport(this.$route.params.id)
        this.$notify({
          type: 'success',
          text: 'Successfully deleted Partner Report',
        })
        this.$router.push({ name: 'data.ledger-beta' })
      } catch (error) {
        console.log(error)
        this.$notify({
          type: 'error',
          text: 'Unable to delete Partner Report',
        })
      }

      this.isLoading = false
    },
    onResetConfirm() {
      this.report = cloneDeep(this.originalReport)
      this.showResetModal = false
      this.$notify({
        type: 'info',
        text: 'Reset Partner Report',
      })
    },
    onSaveConfirm() {
      if (this.isExistingReport) {
        this.updateReport()
      } else {
        this.createReport()
      }
    },
    stepEditable(step) {
      if (step > 2 && this.report.source === 'manual') return false
      return this.step > step || this.isExistingReport
    },
    stepComplete(step) {
      const {
        deal_id,
        partner_slug,
        portal_id,
        source,
        type,
        steps,
        date_format,
        columns: { registration_date, ftd_date, qftd_date, sale_date, basket_date },
      } = this.report
      switch (step) {
        case 1:
          return [source === 'csv' ? deal_id : true, partner_slug, source, portal_id, type].every(Boolean)
        case 2:
          return steps.length && steps.every(Boolean)
        case 3:
          return this.file && this.parsedFile.length
        case 4:
          return [registration_date, ftd_date, qftd_date, sale_date, basket_date].some(Boolean) && date_format
        case 5:
          return true
        case 6:
          if (type !== 'combined') return true
          return type === 'combined'
      }
    },
    async onClickStepTwo() {
      if (this.report.source === 'csv') {
        this.step = 3
        return
      } else {
        this.step = 7
      }
    },
    formattedReportOption(key, value) {
      if (!this.reportOptions) return value
      const option = this.reportOptions[key]?.find(option => option.value === value)
      return option ? option.key : value
    },
    formattedData(key, id) {
      const found = this[key].find(({ value }) => value === id)
      return found ? found.key : id
    },
    getConfigureColumnError(model) {
      if (this.configureColumnErrors.includes(model)) {
        return 'Column contains value not found in attached CSV!'
      }
      return ''
    },
    onAddFilterConfirm() {
      this.report.filters.push(this.newFilter)
      this.newFilter = newRule()
      this.showAddFilterModal = false
    },
    onAddFilterCancel() {
      this.showAddFilterModal = false
      this.newFilter = newRule()
    },
    onAddRuleConfirm() {
      if (!this.newRuleType) return
      if (!this.report.rules[this.newRuleType]) {
        this.$set(this.report.rules, [this.newRuleType], [this.newRule])
      } else {
        this.report.rules[this.newRuleType].push(this.newRule)
      }
      this.newRule = newRule()
      this.showAddRuleModal = false
    },
    onAddRuleCancel() {
      this.showAddRuleModal = false
      this.newRule = newRule()
    },
    onClickDeleteFilter(index) {
      const newVal = this.report.filters.slice(0, index).concat(this.report.filters.slice(index + 1))
      this.$set(this.report, 'filters', newVal)
    },
    onClickDeleteRule(type, index) {
      const newVal = this.report.rules[type].slice(0, index).concat(this.report.rules[type].slice(index + 1))
      this.$set(this.report.rules, type, newVal)
    },
    async updateReport() {
      this.isLoading = true

      try {
        await partnerReports.updateReport(this.$route.params.id, this.report)
        this.showSaveModal = false
        this.$notify({
          type: 'success',
          text: 'Successfully updated Partner Report',
        })
        await this.getReport()
      } catch (error) {
        console.log(error)
        this.$notify({
          type: 'error',
          text: 'Unable to save Partner Report',
        })
      }

      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/settings/_variables';

.table-row {
  border-bottom: thin solid map-get($material-light, 'dividers');

  &--last {
    border-bottom: none;
  }
}

.overview {
  color: map-deep-get($material-light, 'text', 'primary') !important;
}

.sticky {
  position: sticky;
  top: 62px;
  z-index: 4;
}

.full-width {
  min-width: 100%;
}

.stepper ::v-deep .v-stepper__label {
  flex-grow: 1;
}
</style>
